import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Loader2, ChevronLeft, ChevronRight, BadgeCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { optimizeImageUrl } from "@/lib/image-utils";
import type { Listing } from "@/lib/types";

const PAGE_SIZE = 21;

function ListingCard({ listing }: { listing: Listing }) {
  const location = [listing.city, listing.state].filter(Boolean).join(", ");

  return (
    <Link
      to={`/spa/${listing.slug}`}
      className="group flex gap-4 p-4 rounded-2xl border border-border bg-card hover:shadow-xl hover:shadow-black/20 hover:border-primary/20 transition-all duration-300"
    >
      {/* Logo */}
      <div className="w-24 h-24 rounded-xl overflow-hidden bg-white shrink-0">
        {listing.logo_url ? (
          <img
            src={optimizeImageUrl(listing.logo_url, 256)}
            alt={`${listing.name} MedSpa logo${listing.city ? ` in ${listing.city}` : ""}${listing.state ? `, ${listing.state}` : ""}`}
            width={96}
            height={96}
            loading="lazy"
            className="w-full h-full object-contain p-2.5 group-hover:scale-105 transition-transform duration-500"
          />
        ) : listing.image_url ? (
          <img
            src={optimizeImageUrl(listing.image_url, 192)}
            alt={`${listing.name} MedSpa${listing.city ? ` in ${listing.city}` : ""}${listing.state ? `, ${listing.state}` : ""}`}
            width={96}
            height={96}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
            <span className="text-2xl font-display font-bold">
              {listing.name.charAt(0)}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">
            {listing.name}
          </h3>
          {listing.tier !== "free" && (
            <BadgeCheck className="w-4 h-4 text-emerald-500 shrink-0" />
          )}
          {listing.is_featured && (
            <span className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-medium shrink-0">
              Featured
            </span>
          )}
        </div>

        {location && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        )}

        <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:opacity-80 transition-opacity">
          View Details
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-1.5 pt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-30 disabled:pointer-events-none transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {getPages().map((page, i) =>
        page === "..." ? (
          <span key={`dot-${i}`} className="px-2 text-muted-foreground text-sm">...</span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-colors ${
              page === currentPage
                ? "bg-primary text-primary-foreground"
                : "border border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            {page}
          </button>
        )
      )}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-30 disabled:pointer-events-none transition-colors"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function BrowseGrid({
  listings,
  isLoading,
}: {
  listings: Listing[];
  isLoading: boolean;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();

  const totalPages = Math.ceil(listings.length / PAGE_SIZE);
  const paged = listings.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset to page 1 if listings change (e.g. search/filter)
  const listingCount = listings.length;
  const [prevCount, setPrevCount] = useState(listingCount);
  if (listingCount !== prevCount) {
    setPrevCount(listingCount);
    if (currentPage !== 1) setCurrentPage(1);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground text-lg mb-1">No results found</p>
        <p className="text-sm text-muted-foreground/70 mb-6">
          Try adjusting your search or filters
        </p>
        <p className="text-sm text-foreground font-medium mb-3">
          Want to add your listing?
        </p>
        <Link
          to={user ? "/dashboard/listings" : "/register?type=provider"}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
        >
          {user ? "Add Listing" : "Get Listed"}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {paged.map((listing) => (
          <ListingCard key={listing.id} listing={listing} />
        ))}
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
      {totalPages > 1 && (
        <p className="text-center text-xs text-muted-foreground mt-3">
          Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, listings.length)} of {listings.length} listings
        </p>
      )}
    </div>
  );
}
